import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { apiConfigService } from '../services/api';
import '../styles/ConfigApi.css';

const ConfigApi = () => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    active: true
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [cpfToken, setCpfToken] = useState('');
  const [savingCpfToken, setSavingCpfToken] = useState(false);

  useEffect(() => {
    loadCredentials();
    loadLogoPreview();
    loadCpfToken();
  }, []);

  const loadCpfToken = async () => {
    try {
      const data = await apiConfigService.getCpfToken();
      setCpfToken(data.token || '');
    } catch (err) {
      console.error('Erro ao carregar token CPF:', err);
    }
  };

  const handleSaveCpfToken = async () => {
    try {
      setSavingCpfToken(true);
      setError('');
      await apiConfigService.updateCpfToken(cpfToken);
      setSuccess('Token da API de consulta CPF atualizado com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar token');
    } finally {
      setSavingCpfToken(false);
    }
  };

  const loadLogoPreview = () => {
    const logoUrl = apiConfigService.getLogoUrl();
    setLogoPreview(`${logoUrl}?t=${Date.now()}`);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Por favor, selecione uma imagem (JPEG, PNG, GIF, SVG ou WEBP)');
        return;
      }
      
      // Validar tamanho (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('O arquivo deve ter no máximo 5MB');
        return;
      }
      
      setLogoFile(file);
      setError('');
      
      // Criar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      setError('Por favor, selecione um arquivo');
      return;
    }

    try {
      setUploadingLogo(true);
      setError('');
      await apiConfigService.uploadLogo(logoFile);
      setSuccess('Logo enviado com sucesso!');
      // Recarregar preview do logo
      loadLogoPreview();
      setLogoFile(null);
    } catch (error) {
      console.error('Erro ao fazer upload do logo:', error);
      setError(error.response?.data?.error || 'Erro ao fazer upload do logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const loadCredentials = async () => {
    try {
      setLoading(true);
      const data = await apiConfigService.getCredentials();
      setCredentials(data);
    } catch (error) {
      console.error('Erro ao carregar credenciais:', error);
      setError('Erro ao carregar credenciais');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.username || !formData.password) {
      setError('Username e senha são obrigatórios');
      return;
    }

    try {
      if (editingCredential) {
        await apiConfigService.updateCredential(editingCredential.id, formData);
        setSuccess('Credencial atualizada com sucesso');
      } else {
        await apiConfigService.createCredential(formData.username, formData.password);
        setSuccess('Credencial criada com sucesso');
      }

      setFormData({ username: '', password: '', active: true });
      setShowForm(false);
      setEditingCredential(null);
      await loadCredentials();
    } catch (error) {
      console.error('Erro ao salvar credencial:', error);
      setError(error.response?.data?.error || 'Erro ao salvar credencial');
    }
  };

  const handleEdit = (credential) => {
    setEditingCredential(credential);
    setFormData({
      username: credential.username,
      password: '', // Não mostrar senha
      active: credential.active
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja desativar esta credencial?')) {
      return;
    }

    try {
      await apiConfigService.deleteCredential(id);
      setSuccess('Credencial desativada com sucesso');
      await loadCredentials();
    } catch (error) {
      console.error('Erro ao desativar credencial:', error);
      setError(error.response?.data?.error || 'Erro ao desativar credencial');
    }
  };

  const handleToggleActive = async (credential) => {
    try {
      await apiConfigService.updateCredential(credential.id, {
        active: !credential.active
      });
      await loadCredentials();
    } catch (error) {
      console.error('Erro ao atualizar credencial:', error);
      setError(error.response?.data?.error || 'Erro ao atualizar credencial');
    }
  };

  const handleTestConnection = async () => {
    if (!formData.username || !formData.password) {
      setError('Informe username e senha para testar a conexão');
      return;
    }

    try {
      setTestingConnection(true);
      setError('');
      const result = await apiConfigService.testConnection(formData.username, formData.password);
      
      if (result.success) {
        setSuccess('✅ Conexão com API realizada com sucesso!');
      } else {
        setError(result.error || 'Erro ao conectar com a API');
      }
    } catch (error) {
      console.error('Erro ao testar conexão:', error);
      setError(error.response?.data?.error || error.response?.data?.message || 'Erro ao testar conexão');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleCancel = () => {
    setFormData({ username: '', password: '', active: true });
    setShowForm(false);
    setEditingCredential(null);
    setError('');
    setSuccess('');
  };

  return (
    <Layout>
      <div className="config-api-page">
        <div className="page-header">
          <h1>⚙️ Configuração da API de Consulta</h1>
          <p>Gerencie as credenciais para consulta de viabilidade</p>
        </div>

        {error && (
          <div className="alert alert-error">
            ❌ {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            ✅ {success}
          </div>
        )}

        {/* Seção Token API Consulta CPF (DataNexus) */}
        <div className="logo-config-section" style={{ marginBottom: '2rem' }}>
          <h2>🔐 Token API de Consulta CPF (DataNexus)</h2>
          <p>Configure o token para consulta automática de dados por CPF na tela de Nova Venda. Atualize quando o token expirar.</p>
          <div className="form-group" style={{ maxWidth: '500px', marginTop: '1rem' }}>
            <label htmlFor="cpf-token">Token</label>
            <input
              type="text"
              id="cpf-token"
              value={cpfToken}
              onChange={(e) => setCpfToken(e.target.value)}
              placeholder="Cole o token da API DataNexus"
              style={{ width: '100%' }}
            />
            <button
              type="button"
              onClick={handleSaveCpfToken}
              className="btn-primary"
              disabled={savingCpfToken}
              style={{ marginTop: '0.5rem' }}
            >
              {savingCpfToken ? 'Salvando...' : '💾 Salvar Token'}
            </button>
          </div>
        </div>

        {/* Seção de Configuração do Logo */}
        <div className="logo-config-section">
          <h2>🖼️ Configuração do Logo</h2>
          <p>Configure o logo que aparecerá na página de login e nas demais páginas do sistema</p>
          
          <div className="logo-upload-container">
            <div className="logo-preview">
              {logoPreview && (
                <img 
                  src={logoPreview} 
                  alt="Logo preview" 
                  onError={() => setLogoPreview(null)}
                />
              )}
              {!logoPreview && (
                <div className="logo-placeholder">
                  <span>📷</span>
                  <p>Nenhum logo configurado</p>
                </div>
              )}
            </div>
            
            <div className="logo-upload-controls">
              <div className="form-group">
                <label htmlFor="logo-file">Selecione uma imagem (máx. 5MB)</label>
                <input
                  type="file"
                  id="logo-file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml,image/webp"
                  onChange={handleLogoChange}
                  disabled={uploadingLogo}
                />
              </div>
              
              {logoFile && (
                <button
                  type="button"
                  onClick={handleLogoUpload}
                  className="btn-primary"
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? 'Enviando...' : '📤 Enviar Logo'}
                </button>
              )}
            </div>
          </div>
        </div>

        {!showForm && (
          <div className="actions-bar">
            <button onClick={() => setShowForm(true)} className="btn-primary">
              ➕ Nova Credencial
            </button>
          </div>
        )}

        {showForm && (
          <div className="form-container">
            <h2>{editingCredential ? 'Editar Credencial' : 'Nova Credencial'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="username">Username *</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  disabled={editingCredential !== null}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">
                  {editingCredential ? 'Nova Senha (deixe em branco para manter)' : 'Senha *'}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingCredential}
                />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                  />
                  Ativa
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingCredential ? 'Atualizar' : 'Criar'}
                </button>
                <button type="button" onClick={handleCancel} className="btn-secondary">
                  Cancelar
                </button>
                {!editingCredential && (
                  <button
                    type="button"
                    onClick={handleTestConnection}
                    className="btn-test"
                    disabled={testingConnection}
                  >
                    {testingConnection ? 'Testando...' : '🔍 Testar Conexão'}
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="loading">Carregando credenciais...</div>
        ) : (
          <div className="credentials-list">
            <h2>Credenciais Cadastradas</h2>
            {credentials.length === 0 ? (
              <div className="empty-state">
                <p>Nenhuma credencial cadastrada. Clique em "Nova Credencial" para adicionar.</p>
              </div>
            ) : (
              <table className="credentials-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Status</th>
                    <th>Criada em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {credentials.map((credential) => (
                    <tr key={credential.id}>
                      <td>{credential.username}</td>
                      <td>
                        <span className={`badge ${credential.active ? 'badge-success' : 'badge-inactive'}`}>
                          {credential.active ? 'Ativa' : 'Inativa'}
                        </span>
                      </td>
                      <td>
                        {new Date(credential.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                      </td>
                      <td className="actions-cell">
                        <button
                          onClick={() => handleToggleActive(credential)}
                          className="btn-small"
                          title={credential.active ? 'Desativar' : 'Ativar'}
                        >
                          {credential.active ? '⏸️' : '▶️'}
                        </button>
                        <button
                          onClick={() => handleEdit(credential)}
                          className="btn-small btn-edit"
                          title="Editar"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(credential.id)}
                          className="btn-small btn-delete"
                          title="Desativar"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ConfigApi;
